import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import * as ChangePasswordActions from '../../store/actions/change-password.actions';
import * as PasswordActions from '../../store/actions/change-password.actions';
import {
  ApplicationState,
  getPasswordChangeError,
  getSelectedMailbox,
} from '../../../store/reducers';
import { SuccessCode, SuccessInfo } from '../../models/success.model';
import { getPasswordChangeSuccess } from '../../../store/reducers/index';
import { ErrorCode, TechnicalError } from '../../models/errors.model';
import { Alert } from '@proximus/ng-common-components';
import { Mailbox } from '../../models/mailboxes.model';
import { AlertsUtilService } from '../../service/alerts.util.service';
import { LengthValidation } from '../../utils/length-validation';
import { InceptionService } from '@proximus/ng-common';
import { Observable, of, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'mprd-change-password-container',
  templateUrl: 'change-password-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePasswordContainerComponent implements OnInit {
  @ViewChild('pxPassword', { static: false }) pxPassword;

  showLoadingSpinner: boolean;
  changePasswordForm: UntypedFormGroup;
  $passwordClear: Subscription;
  passwordChangeSuccessSubscription: Subscription;
  passwordChangeErrorSubscription: Subscription;
  alerts$: Observable<Alert[]>;
  alerts: Alert[] = [];
  selectedMailbox: string;
  customLengthValidation = LengthValidation;
  resetPasswordFields: boolean;

  constructor(
    private store: Store<ApplicationState>,
    private alertsUtilService: AlertsUtilService,
    private builder: UntypedFormBuilder,
    private changeDetector: ChangeDetectorRef,
    private inceptionService: InceptionService,
    private elementRef: ElementRef,
  ) {}

  ngOnInit() {
    setTimeout(() => {
      this.inceptionService.panelAccordion(this.elementRef);
      this.inceptionService.init();
      this.inceptionService.forms(this.elementRef);
    });

    this.changePasswordForm = this.builder.group({
      password: ['', [Validators.required]],
    });
    this.store
      .select(getSelectedMailbox)
      .pipe(filter(Boolean))
      .subscribe((selectedMailbox: Mailbox) => {
        if (this.selectedMailbox !== selectedMailbox.mbxId) {
          this.selectedMailbox = selectedMailbox.mbxId;
          this.removeAlertByCode(ErrorCode.PasswordValidationError);
          this.removeAlertByCode(ErrorCode.TechnicalError);
          this.removeAlertByCode(SuccessCode.PasswordChangeSuccess);
          this.store.dispatch(new PasswordActions.ResetSuccess());
          this.store.dispatch(new PasswordActions.ResetError());
          this.changeDetector.markForCheck();
        }
      });

    this.passwordChangeErrorSubscription = this.store
      .select(getPasswordChangeError)
      .pipe(filter(Boolean))
      .subscribe((error: TechnicalError) => {
        this.changeDetector.markForCheck();
        this.showLoadingSpinner = false;
        this.updatePasswordError(error);
        this.resetPasswordFields = true;
      });

    this.passwordChangeSuccessSubscription = this.store
      .select(getPasswordChangeSuccess)
      .pipe(filter(Boolean))
      .subscribe((success: SuccessInfo) => {
        this.changeDetector.markForCheck();
        this.showLoadingSpinner = false;
        this.updatePasswordSuccessInfo(success);
        this.resetPasswordFields = true;
      });
    this.alerts$ = of(this.alerts);
  }

  onPasswordFormStatusAndValue(passwordFormStatusAndValue: {
    passwordFormStatus: boolean;
    password: string;
  }) {
    passwordFormStatusAndValue.passwordFormStatus
      ? this.changePasswordForm.patchValue({
          password: passwordFormStatusAndValue.password,
        })
      : this.changePasswordForm.patchValue({ password: '' });
  }

  changePassword() {
    this.resetPasswordFields = false;
    this.showLoadingSpinner = true;
    this.store.dispatch(
      new ChangePasswordActions.ChangePasswordAction(
        this.changePasswordForm.value.password,
      ),
    );
  }

  private updatePasswordError(error: TechnicalError) {
    this.addAlert(
      this.alertsUtilService.getAlertFor(
        'error',
        false,
        'change.password.error.message',
        error.code
          ? ErrorCode.PasswordValidationError
          : ErrorCode.TechnicalError,
      ),
    );
    error.code
      ? this.removeAlertByCode(ErrorCode.TechnicalError)
      : this.removeAlertByCode(ErrorCode.PasswordValidationError);
    this.removeAlertByCode(SuccessCode.PasswordChangeSuccess);
  }

  private updatePasswordSuccessInfo(success: SuccessInfo) {
    this.addAlert(
      this.alertsUtilService.getAlertFor(
        'success',
        false,
        'change.password.success.message',
        success.code,
      ),
    );
    this.removeAlertByCode(ErrorCode.PasswordValidationError);
    this.removeAlertByCode(ErrorCode.TechnicalError);
  }

  private removeAlertByCode(code: SuccessCode | ErrorCode): void {
    if (this.alerts.length > 0) {
      const previousAlerts = this.alerts.filter((alert) => alert.id === code);
      if (previousAlerts.length > 0) {
        this.alerts.splice(this.alerts.indexOf(previousAlerts[0]), 1);
      }
    }
  }

  private addAlert(alert: Alert) {
    if (
      alert &&
      !this.alerts.find((existingAlert) => existingAlert.id === alert.id)
    ) {
      this.alerts.push(alert);
    }
  }

  onCloseAlert(alert: Alert) {
    switch (alert.type) {
      case 'success':
        this.store.dispatch(new PasswordActions.ResetSuccess());
        break;
      case 'error':
        this.store.dispatch(new PasswordActions.ResetError());
        break;
    }
  }
}
